import React, {useContext, useLayoutEffect, useRef, useState} from 'react';
import {Image, Platform} from 'react-native';
import {Camera} from 'expo-camera';
import MainStyle from '../../../../../../MainStyle';
import Button from '../../../../../components/Button';
import {StoreContext} from "../../../../../components/StoreContextProvider";
import AsyncStorage from "@react-native-async-storage/async-storage";
import {isFullSwing, transformImg} from "../../../../../constants";
import {colors} from "../../../../../assets/styles/colors";
import CameraModal from "../../../../../modals/CameraModal";
import ServiceClient from "../../../../../utils/ServiceClient";
import * as ImageManipulator from 'expo-image-manipulator';
import Div from "../../../../../lib/Div";
import P from "../../../../../lib/P";
import Btn from "../../../../../components/Btn";

type Props = { goNextStep: () => void, goCsr: () => void, onCancel: () => void, title: string, subtitle: string };

const IdVerification = (props: Props) => {
  const {title, subtitle} = props;
  const videoRef = useRef<Camera | null>(null);
  const [imageUri, setImageUri] = useState('');
  const Context = useContext(StoreContext);
  const {cancelModalConfig, session} = Context;
  const [cameraModalVisible, setCameraModalVisible] = useState(false);
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);

  const errorModalClose = () => {
    Context.setCancelModalConfig({...cancelModalConfig, visible: false});
  };

  const SubmitPhoto = () => {
    Context.setLoadingOverlay(true);

    ServiceClient.submitPhoto(imageUri).then(isRecognized => {
      if (isRecognized) {
        setTimeout(() => {
          Context.setLoadingOverlay(false);
          props.goNextStep();
        }, 1000);

      } else {
        Context.setLoadingOverlay(false);
        Context.setCancelModalConfig({
          visible: true, title: 'SELFIE ISSUE', leftBtnText: 'RETAKE PHOTO',
          subtitle: 'Something’s up with your selfie… please retake it, looking directly into the camera. If this continues to happen, please contact support.',
          rightBtnText: 'CONTACT SUPPORT', onClose: errorModalClose, onExit: errorModalClose,
          onResume: () => {
            errorModalClose();
            props.goCsr();
          }
        });
      }

    }).catch(err => {
      Context.setLoadingOverlay(false);
      console.log('submitPhoto', err);

      Context.setCancelModalConfig({
        visible: true, title: 'ERROR', subtitle: (err && err.message) ? err.message : 'Failed to submit photo',
        secondary: 'Please contact support', leftBtnText: 'RETAKE PHOTO', rightBtnText: 'CONTACT SUPPORT',
        onClose: errorModalClose, onExit: errorModalClose, onResume: () => {
          errorModalClose();
          props.goCsr();
        }
      });
    });
  };

  const takePicture = () => {
    if (!hasPermission) {
      Context.setLoadingOverlay(true);

      (async () => {
        const {status} = await Camera.requestCameraPermissionsAsync();
        const isGranted = status === 'granted';

        setHasPermission(isGranted);

        if (isGranted) setCameraModalVisible(true);
        Context.setLoadingOverlay(false);
      })();

    } else if (hasPermission && !cameraModalVisible) {
      setCameraModalVisible(true);

    } else if (videoRef.current) {
      const options = {quality: 1, base64: false, exif: false, skipProcessing: true};

      videoRef.current.takePictureAsync(options).then(photo => {
        ImageManipulator.manipulateAsync(photo.uri, [{ flip: ImageManipulator.FlipType.Horizontal }], { compress: 1, base64: true, format: ImageManipulator.SaveFormat.JPEG }).then(res => {
          if (Platform.OS === 'web') {
            if (res.base64) setImageUri(transformImg(res.base64));
            setCameraModalVisible(false);

          } else {
            if (res.base64) setImageUri(transformImg(res.base64));
            setCameraModalVisible(false);
          }
        });
      });

      // const imageSrc = videoRef.current.getScreenshot();
      // if (imageSrc) setImageUri(transformImg(imageSrc));
      // setCameraModalVisible(false);

      // const canvas = document.createElement('canvas');
      // const context = canvas.getContext('2d');
      // if (context) {
      //   canvas.width = videoRef.current.videoWidth;
      //   canvas.height = videoRef.current.videoHeight;
      //   context.translate(canvas.width, 0);
      //   context.scale(-1, 1);
      //
      //   context.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
      //   setImageUri(transformImg(canvas.toDataURL('image/png')));
      //   setCameraModalVisible(false);
      // }
    }
  };

  const next = () => {
    if (imageUri) SubmitPhoto();
  };

  useLayoutEffect(() => {
    if (!session) {
      (async () => {
        const val = await AsyncStorage.getItem('session');

        if (val) {
          const Session = JSON.parse(val);
          Context.setSession(Session);
        }
      })();
    }
  }, []);

  return (
    <Div flex={1} mx={20}>
      {isFullSwing ? (
        <P mt={50} ta="center" fontFamily="DavisSans-Medium">{title}</P>
      ) : (
        <P ta="center" fs={30} fw="500" lh={24}>{title}</P>
      )}

      <Div flex={1} justifyContent="center" minH={351}>
        <Div w={218} h={218} borderColor={colors.gray} borderWidth={2} mx="auto" overflow="hidden" rounded={200}>
          {!!imageUri && <Image style={MainStyle.flex1} source={{uri: `data:image/jpg;base64,${imageUri}`}}/>}
        </Div>
      </Div>

      {!isFullSwing && (<P ta="center" fs={18} fw="500" mb={43}>{subtitle}</P>)}

      {isFullSwing ? (
        <Div flex={1} pt={20} pb={30} justifyContent="flex-end">
          <Btn bg="#3369FF" rounded={4} onPress={imageUri ? next : takePicture}>
            <P lh={56} ta="center" fontFamily="DavisSans-Medium">Accept</P>
          </Btn>
        </Div>
      ) : (
        <>
          <Button
            text={`${imageUri ? 'Retake' : 'Take'} Photo`} w={264} h={63} onPress={takePicture} fontSize={22}
            bg={imageUri ? colors.unselect : colors.newGreenText} color={imageUri ? 'white' : colors.mainBackground}
            fontWeight="600" mb={60} mx="auto"
          />

          <Div row mb={60}>
            <Button
              text="Back" flex={1} h={63} bg="transparent" borderWidth={1} borderColor="white" onPress={props.onCancel}
              fontWeight="600" fontSize={22} color="white"
            />

            <Div w={10}/>

            <Button
              text="Next" flex={1} h={63} onPress={next} bg={imageUri ? colors.newGreenText : colors.unselect}
              color={imageUri ? colors.mainBackground : 'white'} fontWeight="600" fontSize={22}
            />
          </Div>
        </>
      )}

      <CameraModal
        type="photo" videoRef={videoRef} cameraModalVisible={cameraModalVisible}
        takePicture={takePicture} onRequestClose={() => setCameraModalVisible(false)}
      />
    </Div>
  );
};

export default React.memo(IdVerification);
